import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';
import moment from 'moment';
import {toast} from 'react-toastify';
import * as Actions from '../../../store/actions';
import EasyLookupModals from '../EasyLookupModals';
import {
  DashboardLayout,
  PageLimit,
  Pagination,
  PrimaryButton,
  TableLayout,
  EmptyState,
  Loader,
  SecondaryButton,
} from '../../../components';
import FilterV2 from '../../../components/FilterV2';

import {
  corruptedFile,
  eye,
  failed,
  greenCheckBg,
  lookupEmpty,
} from '../../../assets/images/images';
import {mapToOptionsArray, errorCodeColor} from '../../../helpers';
import {getCookieValue} from '../../../helpers/getCookie';
import {PAGES_URL} from '../../../helpers/constants';
import {tableHeader as singleBusinessLookupTableHeader} from '../LookupUtils';
import {OverviewCards} from '../../../components/OverviewCards';
import {shortenLargeNumbers} from '../../../helpers/shortenLargeNumbers';
import {buildTableCardData} from '../../../helpers/buildTableCardData';
import {useLookupHistoryAnalysis} from '../../../hooks/useLookupHistoryAnalysis';
import {useApps} from '../../../hooks/useAllApps';
import {BUSINESS_MODEL_TYPES} from '../../../store/actions';
import Breadcrumbs from 'components/BreadCrumbs';
import PageNumber from '../../../components/PageNumber';
import {useLookupHistory} from '../../../hooks/useLookupHistory';
import {dateRanges} from 'helpers/getDateRange';

function BusinessSingleLookup({
  children,
  fetchLookup,
  changeLookupPage,
  getApiLogs,
  auth: {userDetails},
  lookup: {h_loading},
  getBusinessLookupHistory,
  apiLogs: {logs, loading: apiLoading},
}) {
  const navigate = useNavigate();
  const [showFilter, setShowFilter] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState([]);
  const [openBusinessLookup, setOpenBL] = useState(false);

  const [currentFilters, setCurrentFilters] = useState({
    date: 'seven_days',
    dateRange: dateRanges.seven_days(),
    apps: undefined,
    status: undefined,
    country: undefined,
  });

  const {allAppsData} = useApps();

  const [analysisFilters, setAnalysisFilters] = useState({});
  const {
    analysisData,
    analysisLoading,
    refetch: refetchAnalysis,
  } = useLookupHistoryAnalysis({
    IDENTITY_TYPES: BUSINESS_MODEL_TYPES,
    otherParams: analysisFilters,
  });
  const {
    historyData: businessLookup,
    historyLoading,
    refetch: refetchHistory,
  } = useLookupHistory({
    IDENTITY_TYPES: BUSINESS_MODEL_TYPES,
    otherParams: {...analysisFilters, limit, page: currentPage},
  });

  const env = getCookieValue('env');

  const filterDefinitions = useMemo(() => {
    const definitions = [];

    if (allAppsData && allAppsData.length > 0) {
      definitions.push({
        key: 'apps',
        label: 'Apps',
        type: 'select',
        options: mapToOptionsArray(allAppsData),
        multiSelect: true,
      });
    }

    // definitions.push({
    //   key: 'status',
    //   label: 'Status',
    //   type: 'select',
    //   options: Constants.lookups.status,
    //   multiSelect: false,
    // });
    //
    // definitions.push({
    //   key: 'country',
    //   label: 'Country',
    //   type: 'select',
    //   options: COUNTRIES,
    //   multiSelect: true,
    // });
    return definitions;
  }, [allAppsData]);

  const handleFilterChange = newValuesFromFilterV2 => {
    setData([]);
    setCurrentFilters(() => ({...newValuesFromFilterV2}));
    setCurrentPage(1);
    const queryParams = new URLSearchParams();
    Object.entries(newValuesFromFilterV2).forEach(([key, value]) => {
      if (['apps'].includes(key)) {
        if (Array.isArray(value) && value.length > 0) {
          queryParams.set(key, value.join(','));
        } else if (!Array.isArray(value) && value) {
          queryParams.set(key, value);
        }
      }
    });
    queryParams.set('page', '1');
    navigate(`?${queryParams.toString()}`, {replace: true});
  };

  const pageClick = selected => {
    setCurrentPage(selected);
    setData([]);
  };

  const hasActiveFilters = useMemo(
    () =>
      Object.entries(currentFilters).some(([key, value]) => {
        if (['apps', 'status', 'country'].includes(key)) {
          if (Array.isArray(value)) return value.length > 0;
          return value !== undefined && value !== '';
        }
        return false;
      }),
    [currentFilters],
  );

  useEffect(() => {
    if (env === 'Sandbox') {
      Object.values(userDetails?.permissions?.logPermissions || {}).some(
        value => value,
      ) && getApiLogs({page: currentPage, limit});
    } else if (!children) {
      const apiParams = {
        limit,
        page: currentPage,
        company_id: userDetails?.company?.id,
      };

      if (currentFilters.apps && currentFilters.apps.length > 0) {
        apiParams.apps = currentFilters.apps.join(',');
      } else if (
        allAppsData &&
        allAppsData.length > 0 &&
        !currentFilters.apps
      ) {
        apiParams.apps = allAppsData.map(app => app._id).join(',');
      }

      // if (currentFilters.status) {
      //   apiParams.status = currentFilters.status;
      // }
      // if (currentFilters.country && currentFilters.country.length > 0) {
      //   apiParams.country = currentFilters.country.join(',');
      // }

      if (
        currentFilters.dateRange &&
        currentFilters.dateRange.start &&
        currentFilters.dateRange.end
      ) {
        apiParams.start = currentFilters.dateRange.start;
        apiParams.end = currentFilters.dateRange.end;
      }

      const searchParamsFromUrl = new URLSearchParams(window.location.search);
      const pageParam = searchParamsFromUrl.get('page');
      if (pageParam && parseInt(pageParam) !== currentPage) {
        setCurrentPage(parseInt(pageParam));
        apiParams.page = parseInt(pageParam);
      }

      if (
        userDetails?.company?.id &&
        (apiParams.apps || (allAppsData && allAppsData.length > 0))
      ) {
        const cleanedApiParams = Object.fromEntries(
          Object.entries(apiParams).filter(([, v]) => v !== undefined),
        );

        const newAnalysisFilters = {
          app_ids: cleanedApiParams.apps,
          status: cleanedApiParams.status,
          country: cleanedApiParams.country,
          date_from: cleanedApiParams.start,
          date_to: cleanedApiParams.end,
        };
        const cleanedAnalysisFilters = Object.fromEntries(
          Object.entries(newAnalysisFilters).filter(([, v]) => v !== undefined),
        );
        setAnalysisFilters(cleanedAnalysisFilters);
        refetchAnalysis();
        refetchHistory();
      }
    }
  }, [
    allAppsData,
    currentPage,
    limit,
    userDetails?.company?.id,
    userDetails?.permissions?.logPermissions,
    currentFilters,
    env,
    getApiLogs,
    children,
    refetchAnalysis,
    navigate,
    refetchHistory,
  ]);

  const SANDBOX_DATA = useMemo(() => {
    if (!apiLoading && logs && logs.api_logs) {
      return logs.api_logs.map((log, index) => ({
        entity_name: `Sandbox Business ${index + 1}`,
        entity_type: 'company_reg',
        entity_id: `SB_ID_${index}`,
        history_id: `SB_HIST_${index}`,
        status_code: index % 2 === 0 ? '200' : '404',
        createdAt: log?.createdAt,
      }));
    }
    return [];
  }, [logs, apiLoading]);

  useEffect(() => {
    if (env === 'Sandbox' && !apiLoading && logs) {
      setData(SANDBOX_DATA);
    } else {
      !historyLoading &&
        businessLookup &&
        setData([...(businessLookup?.rows || [])]);
    }
  }, [env, businessLookup, apiLoading, SANDBOX_DATA, historyLoading, logs]);

  const handleExport = () => {
    const apiParams = {
      limit: businessLookup?.count || 10000,
      page: 1,
      company_id: userDetails?.company?.id,
      download: true,
    };

    if (currentFilters.apps && currentFilters.apps.length > 0) {
      apiParams.apps = currentFilters.apps.join(',');
    } else if (allAppsData && allAppsData.length > 0) {
      apiParams.apps = allAppsData.map(app => app._id).join(',');
    }
    if (currentFilters.status) {
      apiParams.status = currentFilters.status;
    }
    if (currentFilters.country && currentFilters.country.length > 0) {
      apiParams.country = currentFilters.country.join(',');
    }
    const cleanedApiParams = Object.fromEntries(
      Object.entries(apiParams).filter(([, v]) => v !== undefined),
    );

    if (userDetails?.company?.id && cleanedApiParams.apps) {
      getBusinessLookupHistory(apiParams);
    } else {
      toast.error('Cannot start export without company and app context.');
    }
  };

  const {performSingleBusinessLookup, downloadBusinessLookup} =
    useMemo(() => userDetails?.permissions?.lookupPermissions, [userDetails]) ||
    {};
  const performRight =
    userDetails?.permissions?.role === 'Owner'
      ? false
      : !performSingleBusinessLookup;

  const EmptyStateComponent = () => {
    const isLoading = historyLoading || (env === 'Sandbox' && apiLoading);
    const isDisabled = !userDetails?.company?.verified || performRight;
    const isNoDataInSandbox =
      env === 'Sandbox' && !apiLoading && data?.length === 0;
    const isEmptyArray = !historyLoading && !apiLoading && data?.length === 0;

    if (isLoading) {
      return <Loader height={50} />;
    }

    const commonEmptyStateProps = {
      onClick: () => setOpenBL(true),
      src: lookupEmpty,
      disabled: isDisabled,
      style: {cursor: isDisabled ? 'not-allowed' : 'pointer'},
      imageHeight: 48,
      imageWidth: 48,
      height: 55,
      buttonText: 'Perform Business Lookup',
    };

    if (hasActiveFilters && isEmptyArray && !isLoading) {
      return (
        <EmptyState
          body={
            'This filter did not return any data, try using different values.'
          }
          src={corruptedFile}
          noBtn
          customBtn={
            <button
              onClick={() => {
                setShowFilter(true);
                setCurrentFilters({
                  apps: undefined,
                  status: undefined,
                  country: undefined,
                });
                navigate(`?page=1`, {replace: true});
              }}
              className="text-brandBlue p-4 text-sm font-medium"
            >
              Clear filters
            </button>
          }
        />
      );
    }

    if (
      (isEmptyArray || isNoDataInSandbox) &&
      !hasActiveFilters &&
      !isLoading
    ) {
      return (
        <EmptyState
          body="No business historical record available"
          {...commonEmptyStateProps}
        />
      );
    }
    return null;
  };

  const transactionsSummary = [
    {
      icon: greenCheckBg,
      alt: 'successful',
      title: 'Successful',
      value: shortenLargeNumbers(analysisData?.success_count || 0, null, {
        startAt: 'M',
        decimals: 0,
      }),
    },
    {
      icon: failed,
      alt: 'failed',
      title: 'Failed',
      value: shortenLargeNumbers(analysisData?.failed_count || 0, null, {
        startAt: 'M',
        decimals: 0,
      }),
    },
  ];

  const tableCardData = buildTableCardData(
    {count: businessLookup?.count, title: 'Total Business Lookups'},
    transactionsSummary,
  );

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Government Lookup</p>
      }
    >
      <EasyLookupModals
        open={openBusinessLookup}
        setOpen={setOpenBL}
        lookupType="Single"
        lookupCategory="Business"
        batch={false}
      />
      <div className="flex items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <div className="flex items-center gap-2">
          <Breadcrumbs
            items={[
              {
                label: 'Business Single Lookup',
                path: PAGES_URL.BUSINESS_LOOKUP,
              },
              {label: 'History'},
            ]}
          />
          {env !== 'Sandbox' && (
            <SecondaryButton
              buttonText="Filter"
              onClick={toggleFilterVisibility}
            />
          )}
        </div>

        {downloadBusinessLookup && (
          <PrimaryButton
            buttonText="Export"
            className="w-[100px]"
            onClick={handleExport}
            disabled={!downloadBusinessLookup}
            title={
              !downloadBusinessLookup
                ? 'You do not have permission to perform this action'
                : ''
            }
            loading={h_loading}
          />
        )}
      </div>

      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2
          filters={filterDefinitions}
          onFilterChange={handleFilterChange}
          initialValues={currentFilters}
        />
      </div>

      {children ?? (
        <>
          <OverviewCards
            cardData={tableCardData}
            overviewLoading={analysisLoading}
            className={`grid md:grid-cols-${
              tableCardData?.length > 3
                ? tableCardData?.length - 1
                : tableCardData?.length > 0
                ? tableCardData.length
                : 1
            } xl:grid-cols-${
              tableCardData?.length > 0 ? tableCardData.length : 1
            } gap-2 lg:gap-4 flex-wrap my-5`}
          />

          <TableLayout negativeMargins>
            <thead className="font-semibold uppercase">
              <tr className="bg-white80">
                {singleBusinessLookupTableHeader.map(({label, key}) => (
                  <th
                    key={key}
                    className={`${
                      label === 'Name'
                        ? 'pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap'
                        : 'pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap'
                    }`}
                  >
                    <div className="flex items-end gap-1 cursor-pointer">
                      <span>{label}</span>
                    </div>
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>

            <tbody className="text-sm bg-white">
              {(historyLoading || (env === 'Sandbox' && apiLoading)) && (
                <tr>
                  <td colSpan={singleBusinessLookupTableHeader.length + 1}>
                    <Loader height={50} />
                  </td>
                </tr>
              )}
              {!(historyLoading || (env === 'Sandbox' && apiLoading)) &&
                data?.length === 0 && (
                  <tr>
                    <td colSpan={singleBusinessLookupTableHeader.length + 1}>
                      <EmptyStateComponent />
                    </td>
                  </tr>
                )}
              {!(historyLoading || (env === 'Sandbox' && apiLoading)) &&
                data &&
                data?.map((record, i) => (
                  <tr
                    onClick={() => {
                      if (record.status_code !== '200') return;
                      setOpenBL(true);
                      changeLookupPage(2);
                      env !== 'Sandbox' &&
                        fetchLookup({
                          id: record?.entity_id,
                          type: record?.entity_type?.toLowerCase(),
                          history_id: record?.history_id,
                        });
                    }}
                    className="border-b cursor-pointer border-grey60 hover:bg-gray-50"
                    key={record?.entity_id || i}
                  >
                    <td className="p-4 pl-4 sm:pl-6 xl:pl-12 text-sm text-body whitespace-nowrap">
                      {record?.entity_name?.toUpperCase() || '-'}
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                      {record?.entity_type?.replaceAll('_', ' ') || '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <div
                        className={`px-2 pt-[5px] pb-[3px] text-xs font-medium uppercase rounded-full w-fit ${
                          errorCodeColor(record?.status_code)?.bg
                        } ${errorCodeColor(record?.status_code)?.color}`}
                      >
                        {record?.status_code === '404'
                          ? 'Not Found'
                          : record?.status_code === '200'
                          ? 'Found'
                          : 'Failed'}
                      </div>
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                      {moment(record?.createdAt).format(
                        'MMM DD, YYYY hh:mmA',
                      ) || '-'}
                    </td>
                    {record?.status_code === '200' && (
                      <td className="p-4 pl-0 whitespace-nowrap">
                        <div className="flex items-center gap-4">
                          <button title="View" className="h-4 aspect-square">
                            <img src={eye} alt="View" />
                          </button>
                        </div>
                      </td>
                    )}
                    {record?.status_code !== '200' && (
                      <td className="p-4 pl-0 whitespace-nowrap"></td>
                    )}
                  </tr>
                ))}
            </tbody>
          </TableLayout>

          {!(historyLoading || (env === 'Sandbox' && apiLoading)) &&
            data?.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 -mx-8 px-8 border-t border-[#E4E3EC] py-[18px]">
                <PageNumber
                  currentPage={+currentPage}
                  limit={limit}
                  totalCount={businessLookup?.count}
                />
                <div className="flex items-center gap-5">
                  <PageLimit
                    onLimitChange={l => {
                      setLimit(l);
                      setCurrentPage(1);
                    }}
                    total={businessLookup?.count}
                    length={businessLookup?.rows?.length}
                    limit={limit}
                  />
                  <Pagination
                    total={
                      businessLookup && Math.ceil(businessLookup?.count / limit)
                    }
                    current={+currentPage}
                    onPageChange={activePage => {
                      pageClick(activePage);
                    }}
                  />
                </div>
              </div>
            )}
        </>
      )}
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(BusinessSingleLookup);
